import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dropdown, Input, Modal, Tooltip, message } from "antd";
import {
  CheckCircle,
  Note,
  ChatCircleText,
  MoonStars,
  CaretDown,
  ArrowCounterClockwise,
  ArrowsLeftRight,
  GraduationCap,
  Paperclip,
  Scales,
} from "@phosphor-icons/react";
import {
  fetchInboxItems,
  fetchInboxUnreadCount,
  inboxListFetchArgs,
  markInboxItemUnread,
  selectInboxMutationsForDocument,
  selectNextInboxDocumentId,
  snoozeInboxItemThunk,
  unsnoozeInboxItemThunk,
} from "../../slice/InboxSlice";
import {
  InboxActionFooterContainer,
  InboxActionFooterPrimaryGroup,
  InboxActionFooterSecondaryGroup,
  InboxFooterIconButton,
} from "./AIPaneStyled";
import AssignTaskModal from "../Inbox/AssignTaskModal";
import SendMessageModal from "../Inbox/SendMessageModal";
import SnoozeCustomModal from "../Inbox/SnoozeCustomModal";
import CreateWorkItemModal from "../Inbox/CreateWorkItemModal";
import AttachToWorkItemModal from "../Inbox/AttachToWorkItemModal";
import useAuth from "../../hooks/useAuth";
import useMarkInboxReviewed from "../../hooks/useMarkInboxReviewed";
import { requestBoardAppealReview } from "../../service/inbox";
import { AddNotificationLetterModal } from "../Categorization/NotificationLetterModal";
import { canCurateNotificationLetters } from "../Categorization/notificationLetterCuration";
import { addNotificationLetterExemplar } from "../../service/matter";

/**
 * Inbox action footer (FE Phase 5b — write surface).
 *
 * Pinned at the bottom of the AIPane in inbox mode. Surfaces the four
 * v1 actions agreed during 2026-04-26 mockup review:
 *
 *   1. Mark reviewed (primary CTA, single click)        → mark-reviewed
 *      ↳ becomes "Mark unread" (icon flips) when the row is already
 *        marked reviewed, so the action is reversible without leaving
 *        the surface.
 *   2. Create Task         (opens AssignTaskModal)      → tasks/
 *   3. Send message        (opens SendMessageModal)     → messages/
 *   4. Snooze              (antd Dropdown with presets) → snooze/
 *      ↳ flips to "Unsnooze" while a snooze is active.
 *
 * 2 hits the same create-task endpoint. Assignments were folded back
 * into Tasks (Matthew, 2026-08-18), so there is one button.
 *
 * Plus Work Item (Matthew, 2026-08-25): opens CreateWorkItemModal,
 * which writes to the NEW Salesforce org's attorney→paralegal loop —
 * migration-aware, so it explains itself on unmigrated matters while
 * Create Task keeps covering Litify.
 *
 * Plus Attach (2026-08-28, the conveyor belt): opens
 * AttachToWorkItemModal to attach this document to an open work item
 * on its matter, which volleys the item back to the requester's court
 * with the document linked. The WaitingOnBanner above the tabs offers
 * the same move preselected per item; this button is the generic entry.
 *
 * Open in matter is exposed via `InboxAIHeader` rather than the
 * footer (it sits next to the doc/matter title) so the footer stays
 * focused on per-doc state changes.
 *
 * State sourcing
 * --------------
 * The footer reads `is_read` / `snoozed_until` from the row payload
 * (`item` prop) — the parent (`AIPane` in inbox mode, via `Inbox`)
 * hands the freshest copy down. After a mutation, the slice patches
 * the row in place, or removes it from the default view when an active
 * snooze is applied, so the next render sees the new state. The
 * unread-count badge is reconciled by dispatching
 * `fetchInboxUnreadCount` after each read-state / snooze mutation
 * (the optimistic local delta bridges the request latency).
 *
 * Disabled / busy states are driven by per-doc mutation flags from
 * `selectInboxMutationsForDocument` so a button shows a spinner
 * exactly when its own mutation is in flight (rather than gating on
 * a global "any inbox call running" flag).
 *
 * Layouts
 * -------
 * `layout="row"` (default) is the horizontal footer described above.
 * `layout="rail"` (Matthew, 2026-10-08) is the collapsed right rail:
 * the same actions, same handlers, same modals — rendered as a
 * vertical column of icon-only buttons with the label in a tooltip,
 * so collapsing the AI panel never takes the actions away.
 */

export const INBOX_ACTION_FOOTER_LAYOUT_ROW = "row";
export const INBOX_ACTION_FOOTER_LAYOUT_RAIL = "rail";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const SNOOZE_PRESETS = Object.freeze([
  { key: "4h", label: "4 hours", deltaMs: 4 * HOUR_MS },
  { key: "24h", label: "1 day", deltaMs: 24 * HOUR_MS },
  { key: "3d", label: "3 days", deltaMs: 3 * DAY_MS },
  { key: "1w", label: "1 week", deltaMs: 7 * DAY_MS },
]);

const InboxActionFooter = ({
  documentId,
  item,
  hideCreateTask = false,
  onWorkItemAttached = undefined,
  checkedDocumentIds = [],
  onClearChecked = undefined,
  layout = INBOX_ACTION_FOOTER_LAYOUT_ROW,
}) => {
  const isRail = layout === INBOX_ACTION_FOOTER_LAYOUT_RAIL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const markReviewed = useMarkInboxReviewed();
  // Store handle (not a subscription): the next-document lookup reads
  // list state at CLICK time, after the slice patched is_read, so a
  // render-time selector would be one mutation stale.
  const store = useStore();
  // Which kind the shared modal is open for, or null when closed. One
  // piece of state rather than two booleans, so the two buttons cannot
  // both be "open" at once.
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [snoozeCustomOpen, setSnoozeCustomOpen] = useState(false);
  const [workItemModalOpen, setWorkItemModalOpen] = useState(false);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [appealOpen, setAppealOpen] = useState(false);
  const [appealNote, setAppealNote] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);
  const isBoardDecision = Boolean(item?.is_board_decision);

  const sendForAppealReview = useCallback(async () => {
    const trimmed = appealNote.trim();
    if (!trimmed) {
      message.warning("Add a note so he knows why this was sent.");
      return;
    }
    setAppealBusy(true);
    try {
      await requestBoardAppealReview(documentId, trimmed);
      message.success("Sent for appeal review.");
      setAppealOpen(false);
      setAppealNote("");
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Couldn't send this for review."
      );
    } finally {
      setAppealBusy(false);
    }
  }, [appealNote, documentId]);

  // Curator-only teaching action ("Teach AI"): adds the open letter to
  // the Notification Letter exemplar library without leaving the inbox.
  // Renders for the curator allowlist only (Matthew, 2026-08-12: "it
  // should only be added for me"); the backend enforces the same gate.
  const { user } = useAuth();
  const canTeach = canCurateNotificationLetters(user);
  const [teachModalOpen, setTeachModalOpen] = useState(false);
  const [teachSaving, setTeachSaving] = useState(false);
  const [teachSuccess, setTeachSuccess] = useState(false);
  const [teachError, setTeachError] = useState("");

  const handleTeachSubmit = useCallback(
    async (payload) => {
      setTeachSaving(true);
      setTeachError("");
      try {
        await addNotificationLetterExemplar(payload);
        setTeachSuccess(true);
        message.success("Added to the notification letter library.");
        setTimeout(() => {
          setTeachModalOpen(false);
          setTeachSuccess(false);
        }, 600);
      } catch (err) {
        setTeachError(
          err?.response?.data?.error ||
            "Couldn't add to the library — try again."
        );
      } finally {
        setTeachSaving(false);
      }
    },
    []
  );

  const mutations = useSelector((state) =>
    selectInboxMutationsForDocument(state, documentId)
  );

  const isRead = Boolean(item?.is_read);
  const snoozedUntil = item?.snoozed_until || null;
  const snoozeActive = useMemo(() => {
    if (!snoozedUntil) return false;
    const ts = Date.parse(snoozedUntil);
    if (Number.isNaN(ts)) return false;
    return ts > Date.now();
  }, [snoozedUntil]);

  const readBusy = mutations.readState === "loading";
  const snoozeBusy = mutations.snooze === "loading";
  const taskBusy = mutations.task === "loading";
  const messageBusy = mutations.message === "loading";

  const handleMarkReviewed = useCallback(() => {
    if (!documentId || readBusy) return;
    const selected = (checkedDocumentIds || []).filter(Boolean);
    const leavingIds = selected.length > 1 ? selected : [documentId];
    if (isRead && leavingIds.length === 1) {
      dispatch(markInboxItemUnread({ documentId }))
        .unwrap()
        .then(() => {
          dispatch(fetchInboxUnreadCount());
          dispatch(fetchInboxItems(inboxListFetchArgs(store.getState())));
        })
        .catch((err) => {
          message.error(err?.message || "Couldn't update read state");
        });
      return;
    }
    markReviewed(leavingIds)
      .then(() => {
        onClearChecked?.();
      })
      .catch((err) => {
        message.error(err?.message || "Couldn't update read state");
      });
  }, [
    checkedDocumentIds,
    dispatch,
    documentId,
    isRead,
    markReviewed,
    onClearChecked,
    readBusy,
    store,
  ]);

  const handleSnoozeSuccess = useCallback((nextDocumentId) => {
    dispatch(fetchInboxUnreadCount());

    const postMutationState = store.getState();
    const filters = inboxListFetchArgs(postMutationState);
    if (filters.includeSnoozed) return;

    // Active snoozes leave the default inbox immediately. Move the
    // triage view off the hidden document and backfill the current page.
    navigate(nextDocumentId ? `/inbox/${nextDocumentId}` : "/inbox");
    dispatch(fetchInboxItems(filters));
  }, [dispatch, navigate, store]);

  const handleSnoozePreset = useCallback(
    (presetKey) => {
      if (!documentId || snoozeBusy) return;
      if (presetKey === "custom") {
        setSnoozeCustomOpen(true);
        return;
      }
      const preset = SNOOZE_PRESETS.find((p) => p.key === presetKey);
      if (!preset) return;
      const until = new Date(Date.now() + preset.deltaMs).toISOString();
      // Capture the next item before the fulfilled reducer removes the
      // current row; after removal, list order is no longer recoverable.
      const nextDocumentId = selectNextInboxDocumentId(
        store.getState(),
        documentId
      );
      dispatch(snoozeInboxItemThunk({ documentId, until }))
        .unwrap()
        .then(() => {
          message.success(`Snoozed for ${preset.label}`);
          handleSnoozeSuccess(nextDocumentId);
        })
        .catch((err) => {
          message.error(err?.message || "Couldn't snooze");
        });
    },
    [dispatch, documentId, handleSnoozeSuccess, snoozeBusy, store]
  );

  const handleUnsnooze = useCallback(() => {
    if (!documentId || snoozeBusy) return;
    dispatch(unsnoozeInboxItemThunk({ documentId }))
      .unwrap()
      .then(() => {
        message.success("Snooze cleared");
        dispatch(fetchInboxUnreadCount());
      })
      .catch((err) => {
        message.error(err?.message || "Couldn't clear snooze");
      });
  }, [dispatch, documentId, snoozeBusy]);

  const handleSnoozeCustomConfirm = useCallback(
    (untilIso) => {
      if (!documentId || !untilIso) return;
      const nextDocumentId = selectNextInboxDocumentId(
        store.getState(),
        documentId
      );
      dispatch(snoozeInboxItemThunk({ documentId, until: untilIso }))
        .unwrap()
        .then(() => {
          setSnoozeCustomOpen(false);
          message.success("Snoozed until selected time");
          handleSnoozeSuccess(nextDocumentId);
        })
        .catch((err) => {
          message.error(err?.message || "Couldn't snooze");
        });
    },
    [dispatch, documentId, handleSnoozeSuccess, store]
  );

  // antd Dropdown items API. We render the unsnooze entry conditionally
  // at the top so it's the most prominent option when a snooze is
  // already active.
  const snoozeMenuItems = useMemo(() => {
    const items = [];
    if (snoozeActive) {
      items.push({
        key: "unsnooze",
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ArrowCounterClockwise size={14} weight="bold" />
            Clear snooze
          </span>
        ),
      });
      items.push({ type: "divider" });
    }
    SNOOZE_PRESETS.forEach((preset) => {
      items.push({
        key: preset.key,
        label: preset.label,
      });
    });
    items.push({ type: "divider" });
    items.push({ key: "custom", label: "Custom…" });
    return items;
  }, [snoozeActive]);

  const handleSnoozeMenuClick = useCallback(
    ({ key }) => {
      if (key === "unsnooze") {
        handleUnsnooze();
        return;
      }
      handleSnoozePreset(key);
    },
    [handleSnoozePreset, handleUnsnooze]
  );

  if (!documentId) return null;

  const multiCount = (checkedDocumentIds || []).length;
  const reviewLabel =
    !isRead && multiCount > 1
      ? `Mark ${multiCount} reviewed`
      : isRead
        ? "Mark unread"
        : "Mark reviewed";
  const reviewText = readBusy ? "Updating…" : reviewLabel;
  const snoozeLabel = snoozeActive ? "Snoozed — change" : "Snooze";
  const snoozeText = snoozeActive ? "Snoozed" : "Snooze";

  // In the rail the label lives in a tooltip (placed to the left, away
  // from the screen edge); in the row it's inline text. Keeping the
  // aria-label on the button in both layouts means tests and screen
  // readers see the same control either way.
  const withLabel = (title, button) =>
    isRail ? (
      <Tooltip title={title} placement="left" mouseEnterDelay={0.2}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  const text = (label) => (isRail ? null : label);

  return (
    <>
      <InboxActionFooterContainer
        aria-label="Inbox actions"
        $rail={isRail}
        data-layout={layout}
      >
        <InboxActionFooterPrimaryGroup $rail={isRail}>
          {withLabel(
            reviewText,
            <InboxFooterIconButton
              type="button"
              onClick={handleMarkReviewed}
              disabled={readBusy}
              aria-label={reviewLabel}
              $rail={isRail}
              style={
                isRead
                  ? undefined
                  : {
                      color: "#0C53BB",
                      borderColor: "#B9D2F0",
                      background: "#F1F6FE",
                    }
              }
            >
              <CheckCircle
                size={16}
                weight={isRead && multiCount <= 1 ? "regular" : "fill"}
              />
              {text(reviewText)}
            </InboxFooterIconButton>
          )}
        </InboxActionFooterPrimaryGroup>

        <InboxActionFooterSecondaryGroup $rail={isRail}>
          {isBoardDecision &&
            withLabel(
              "Appeal review",
              <InboxFooterIconButton
                type="button"
                onClick={() => setAppealOpen(true)}
                aria-label="Send for appeal review"
                data-testid="inbox-appeal-review-button"
                $rail={isRail}
              >
                <Scales size={16} />
                {text("Appeal review")}
              </InboxFooterIconButton>
            )}
          {/*
            One door (2026-08-28): on matters migrated to the new
            Salesforce, accountable work goes through Work Items — the
            Litify-bound Create Task hides so nobody writes a task into
            the system the matter has left. Unmigrated matters keep it;
            their tasks still live in Litify. `hideCreateTask` comes
            from AIPane's waiting-on fetch (matter_found).
          */}
          {!hideCreateTask &&
            withLabel(
              "Create Task",
              <InboxFooterIconButton
                type="button"
                onClick={() => setTaskModalOpen(true)}
                disabled={taskBusy}
                aria-label="Create Task"
                $rail={isRail}
              >
                <Note size={16} />
                {text("Create Task")}
              </InboxFooterIconButton>
            )}

          {withLabel(
            "Work Item",
            <InboxFooterIconButton
              type="button"
              onClick={() => setWorkItemModalOpen(true)}
              aria-label="Create Work Item"
              data-testid="inbox-work-item-button"
              $rail={isRail}
            >
              <ArrowsLeftRight size={16} />
              {text("Work Item")}
            </InboxFooterIconButton>
          )}

          {withLabel(
            "Attach to work item",
            <InboxFooterIconButton
              type="button"
              onClick={() => setAttachModalOpen(true)}
              aria-label="Attach to work item"
              data-testid="inbox-attach-work-item-button"
              $rail={isRail}
            >
              <Paperclip size={16} />
              {text("Attach")}
            </InboxFooterIconButton>
          )}

          {withLabel(
            "Send message",
            <InboxFooterIconButton
              type="button"
              onClick={() => setMessageModalOpen(true)}
              disabled={messageBusy}
              aria-label="Send message"
              $rail={isRail}
            >
              <ChatCircleText size={16} />
              {text("Send message")}
            </InboxFooterIconButton>
          )}

          {/*
            The Dropdown owns the button's hover/click wiring, so the
            rail uses the native `title` for its label instead of
            nesting a second antd trigger around the same element.
          */}
          <Dropdown
            menu={{ items: snoozeMenuItems, onClick: handleSnoozeMenuClick }}
            trigger={["click"]}
            disabled={snoozeBusy}
            placement={isRail ? "leftBottom" : "top"}
          >
            <InboxFooterIconButton
              type="button"
              disabled={snoozeBusy}
              aria-label={snoozeLabel}
              title={isRail ? snoozeText : undefined}
              $rail={isRail}
            >
              <MoonStars size={16} weight={snoozeActive ? "fill" : "regular"} />
              {text(snoozeText)}
              {!isRail && <CaretDown size={10} weight="bold" />}
            </InboxFooterIconButton>
          </Dropdown>

          {canTeach &&
            withLabel(
              "Teach AI",
              <InboxFooterIconButton
                type="button"
                onClick={() => {
                  setTeachError("");
                  setTeachSuccess(false);
                  setTeachModalOpen(true);
                }}
                aria-label="Teach AI: add as notification letter example"
                data-testid="inbox-teach-ai-button"
                $rail={isRail}
              >
                <GraduationCap size={16} />
                {text("Teach AI")}
              </InboxFooterIconButton>
            )}
        </InboxActionFooterSecondaryGroup>
      </InboxActionFooterContainer>

      <Modal
        open={appealOpen}
        title="Send this Board decision for appeal review"
        okText="Send"
        onCancel={appealBusy ? undefined : () => setAppealOpen(false)}
        onOk={sendForAppealReview}
        okButtonProps={{ loading: appealBusy }}
        cancelButtonProps={{ disabled: appealBusy }}
      >
        <p>
          It goes to Matthew’s document review. Add what he should know.
        </p>
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 8 }}
          value={appealNote}
          onChange={(event) => setAppealNote(event.target.value)}
          maxLength={4000}
          placeholder="What the Board did, and why this needs a look…"
          disabled={appealBusy}
        />
      </Modal>
      <AssignTaskModal
        open={taskModalOpen}
        documentId={documentId}
        onClose={() => setTaskModalOpen(false)}
      />
      <SendMessageModal
        open={messageModalOpen}
        documentId={documentId}
        onClose={() => setMessageModalOpen(false)}
      />
      <CreateWorkItemModal
        open={workItemModalOpen}
        documentId={documentId}
        onClose={() => setWorkItemModalOpen(false)}
      />
      <AttachToWorkItemModal
        open={attachModalOpen}
        documentId={documentId}
        onClose={() => setAttachModalOpen(false)}
        onAttached={onWorkItemAttached}
      />
      <SnoozeCustomModal
        open={snoozeCustomOpen}
        onClose={() => setSnoozeCustomOpen(false)}
        onConfirm={handleSnoozeCustomConfirm}
        busy={snoozeBusy}
      />
      {canTeach && (
        <AddNotificationLetterModal
          open={teachModalOpen}
          saving={teachSaving}
          success={teachSuccess}
          error={teachError}
          presetDocument={{
            id: String(documentId),
            filename: item?.name || "",
          }}
          onCancel={() => {
            if (teachSaving) return;
            setTeachModalOpen(false);
            setTeachError("");
          }}
          onSubmit={handleTeachSubmit}
        />
      )}
    </>
  );
};

InboxActionFooter.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    is_read: PropTypes.bool,
    snoozed_until: PropTypes.string,
  }),
  hideCreateTask: PropTypes.bool,
  onWorkItemAttached: PropTypes.func,
  checkedDocumentIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onClearChecked: PropTypes.func,
  layout: PropTypes.oneOf([
    INBOX_ACTION_FOOTER_LAYOUT_ROW,
    INBOX_ACTION_FOOTER_LAYOUT_RAIL,
  ]),
};

export default InboxActionFooter;
